import React from 'react';

/** Whether a row that scrolls sideways has more of itself past its start and past its end. */
export type ScrollEdges = { start: boolean; end: boolean };

const NONE: ScrollEdges = { start: false, end: false };

/**
 * Tracks how much of a horizontally scrolling element lies past each edge, for a fade on the edges that hide
 * something (`scrollFadeMaskX`). Synchronises with the element's scroll, its size and its content's size;
 * `contentKey` changes whenever its content does, so a row that gains or loses items is measured again.
 */
export const useScrollEdges = (ref: React.RefObject<HTMLElement>, contentKey: unknown): ScrollEdges => {
  const [edges, setEdges] = React.useState<ScrollEdges>(NONE);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = (): void => {
      // A pixel of slack either side: a fractional scroll position never quite reaches the end.
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((was) => (was.start === start && was.end === end ? was : { start, end }));
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
  }, [ref, contentKey]);

  return edges;
};
